import { useState, useEffect, useCallback, useRef } from 'react';
import ProgressiveImage from '@/components/base/ProgressiveImage';

const slides = [
  {
    id: 1,
    image: '/media/e3506ccf-2c82-45bc-b2ec-0ec22e14af1f_a-cd3919fe73.png',
    badge: "50 Yıllık Aile Firması · 1975'ten Bugüne",
    heading: "Baba'dan Oğula\nAynı Güven,\nAynı Sorumluluk",
    highlight: 'Aynı Güven,',
    sub: "1975'ten bu yana baba-oğul ortaklığıyla hizmet veriyoruz. Ev taşıma, parsiyel ve kargo taşımacılığında 50 yıllık deneyim — Türkiye'nin 81 ilinden Avrupa'nın 27 ülkesine.",
    cta: 'Hemen Fiyat Al',
    ctaHref: 'https://wa.me/905322376576?text=Merhaba%2C%20fiyat%20teklifi%20almak%20istiyorum.',
  },
  {
    id: 2,
    image: '/media/search-image-31bc0a707a.jpg',
    badge: 'Kapıdan Kapıya Teslimat',
    heading: "Aldığımız Gibi\nTeslim Ediyoruz —\nAnahtar Teslim",
    highlight: 'Anahtar Teslim',
    sub: "Türkiye'nin her köşesinden aldığımız yükleri Avrupa'nın istediğiniz adresine eksiksiz, hasarsız ve zamanında ulaştırıyoruz.",
    cta: 'WhatsApp ile Yaz',
    ctaHref: 'https://wa.me/905322376576',
  },
  {
    id: 3,
    image: '/media/search-image-65181150b5.jpg',
    badge: 'Almanya Merkez Depo',
    heading: "Almanya'daki Depomuzdan\nTüm Avrupa'ya\nHızlı Dağıtım",
    highlight: "Tüm Avrupa'ya",
    sub: "Almanya'daki merkez depomuz sayesinde Fransa'dan İspanya'ya, Hollanda'dan Polonya'ya — 27 ülkeye güvenli ve hızlı dağıtım yapıyoruz.",
    cta: 'Hizmetlerimizi Gör',
    ctaHref: '/hizmetlerimiz',
  },
  {
    id: 4,
    image: '/media/search-image-8b5cc21f4f.jpg',
    badge: 'Ev & Ofis Taşıma',
    heading: "Evinizi Taşıyoruz\nPaketlemeden Kuruluma\nHer Şey Dahil",
    highlight: 'Her Şey Dahil',
    sub: "Mobilyalarınızı söküyor, özel ambalajlıyor, yeni adresinizde kuruyoruz. Türkiye'den Avrupa'ya stressiz taşınma deneyimi.",
    cta: 'Detaylı Bilgi Al',
    ctaHref: '/hizmetlerimiz/ev-ofis-tasima',
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [contentKey, setContentKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((index: number) => {
    setCurrent(index);
    setContentKey((k) => k + 1);
  }, []);

  const next = useCallback(() => {
    setCurrent((c) => {
      const n = (c + 1) % slides.length;
      setContentKey((k) => k + 1);
      return n;
    });
  }, []);

  const prev = useCallback(() => {
    setCurrent((c) => {
      const n = (c - 1 + slides.length) % slides.length;
      setContentKey((k) => k + 1);
      return n;
    });
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(next, 5500);
  }, [next]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  const handleGoTo = (index: number) => {
    goTo(index);
    resetTimer();
  };

  const handlePrev = () => {
    prev();
    resetTimer();
  };

  const handleNext = () => {
    next();
    resetTimer();
  };

  const slide = slides[current];
  const headingParts = slide.heading.split('\n');

  return (
    <section
      id="home"
      className="relative overflow-hidden"
      style={{ height: '100vh', minHeight: '640px' }}
    >
      {/* ── Tüm slide arka planları üst üste, sabit absolute ── */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          aria-hidden={i !== current}
          style={{
            position: 'absolute',
            inset: 0,
            transition: 'opacity 0.8s ease',
            opacity: i === current ? 1 : 0,
            zIndex: 0,
          }}
        >
          <ProgressiveImage
            src={s.image}
            alt={s.badge}
            className="object-cover object-center"
            priority={i === 0}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.60) 55%, rgba(0,0,0,0.18) 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 50%, rgba(0,0,0,0.18) 100%)' }} />
        </div>
      ))}

      {/* ── İçerik — sabit absolute, layout'u etkilemez ── */}
      <div
        style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center' }}
      >
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6" style={{ paddingTop: '7rem', paddingBottom: '5rem' }}>
          <div className="max-w-3xl">

            {/* Badge */}
            <div
              key={`badge-${contentKey}`}
              className="inline-flex items-center gap-2.5 border border-white/25 text-white/80 px-4 py-2 rounded-sm text-xs font-medium tracking-widest uppercase mb-8 animate-fadeInUp"
            >
              <span className="w-1.5 h-1.5 bg-brand-orange rounded-full animate-pulse" />
              {slide.badge}
            </div>

            {/* Heading */}
            <h1
              key={`heading-${contentKey}`}
              className="text-white font-heading font-bold text-4xl md:text-5xl lg:text-[3.6rem] leading-[1.1] mb-6 tracking-tight animate-fadeInUp"
              style={{ animationDelay: '0.08s' }}
            >
              {headingParts.map((part, idx) => (
                <span key={idx}>
                  {part === slide.highlight
                    ? <span className="text-brand-orange">{part}</span>
                    : part}
                  {idx < headingParts.length - 1 && <br />}
                </span>
              ))}
            </h1>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-0.5 bg-brand-orange" />
              <div className="w-3 h-0.5 bg-white/20" />
            </div>

            {/* Sub */}
            <p
              key={`sub-${contentKey}`}
              className="text-white/70 text-base md:text-lg mb-10 leading-relaxed max-w-xl animate-fadeInUp"
              style={{ animationDelay: '0.16s' }}
            >
              {slide.sub}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <a
                href={slide.ctaHref}
                target={slide.ctaHref.startsWith('http') ? '_blank' : undefined}
                rel={slide.ctaHref.startsWith('http') ? 'nofollow noreferrer' : undefined}
                className="group flex items-center gap-3 bg-brand-orange hover:bg-orange-600 text-white px-7 py-3.5 rounded-sm font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform" />
                </span>
                {slide.cta}
              </a>
              <a
                href="https://wa.me/905322376576"
                target="_blank"
                rel="nofollow noreferrer"
                className="group flex items-center gap-3 bg-transparent hover:bg-white/10 text-white border border-white/40 hover:border-white px-7 py-3.5 rounded-sm font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-lg" />
                </span>
                WhatsApp ile Yaz
              </a>
              <a
                href="tel:+905322376576"
                className="group flex items-center gap-3 bg-transparent hover:bg-white/10 text-white/70 hover:text-white border border-white/20 hover:border-white/50 px-7 py-3.5 rounded-sm font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-lg" />
                </span>
                +90 532 237 65 76
              </a>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-10 mt-14 pt-10 border-t border-white/10">
              {[
                { value: '50+', label: 'Yıllık Deneyim' },
                { value: '27', label: 'Avrupa Ülkesi' },
                { value: '81', label: 'Türkiye İli' },
                { value: '7/24', label: 'Aktif Destek' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-white font-heading font-bold text-2xl md:text-3xl">{stat.value}</div>
                  <div className="text-white/40 text-xs tracking-wide mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Ok Butonları ── */}
      <button
        onClick={handlePrev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center border border-white/30 hover:border-white bg-black/20 hover:bg-black/40 text-white rounded-full transition-all duration-200 cursor-pointer"
        aria-label="Önceki"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={handleNext}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center border border-white/30 hover:border-white bg-black/20 hover:bg-black/40 text-white rounded-full transition-all duration-200 cursor-pointer"
        aria-label="Sonraki"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* ── Dot Göstergeler ── */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3">
        <div className="flex items-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => handleGoTo(i)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                i === current ? 'w-8 h-2 bg-brand-orange' : 'w-2 h-2 bg-white/40 hover:bg-white/70'
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
        <span className="text-white/30 text-xs tracking-widest">
          {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
        </span>
      </div>

      {/* ── Sağ Etiketler (masaüstü) ── */}
      <div className="absolute right-6 top-1/2 -translate-y-1/2 z-20 hidden lg:flex flex-col gap-3">
        {slides.map((s, i) => (
          <button
            key={i}
            onClick={() => handleGoTo(i)}
            className={`flex items-center gap-2 group cursor-pointer transition-all duration-300 ${i === current ? 'opacity-100' : 'opacity-30 hover:opacity-60'}`}
          >
            <span className={`text-xs text-white font-medium whitespace-nowrap transition-all duration-300 ${i === current ? 'translate-x-0' : 'translate-x-2 group-hover:translate-x-0'}`}>
              {s.badge}
            </span>
            <span className={`w-0.5 rounded-full transition-all duration-300 ${i === current ? 'h-8 bg-brand-orange' : 'h-3 bg-white/50'}`} />
          </button>
        ))}
      </div>
    </section>
  );
}
